import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import Loading from 'app/loading';
import { useIsSideBarClosed } from 'ui/hooks';
import { useMobileCheckHook } from 'story-media/hooks';

import { Page, Content, Heading, CardGrid, ConfirmBar, BackButton, PrimaryButton, ErrorText } from './styled';
import { DashboardTopNav, DashboardBottomNav } from './chrome';
import DashboardTabs, {
  TAB_UNASSIGNED,
  TAB_MY_BOOKS,
  TAB_GIFTS,
  TAB_ORDER,
} from './tabs';
import AddOnSelector from './add-on-selector';
import UnassignedBookTile from './unassigned/book-tile';
import UnassignedJournalTile from './unassigned/journal-tile';
import BookCard from './my-books/book-card';
import GiftCard, { giftTitle } from './gifts/gift-card';
import EmptyState from './empty-states';
import DetailsModal from './details-modal';
import Chevron from './chevron';
import {
  useUnassignedBookEntitlements,
  useUnassignedAddOnEntitlements,
  useMyChildJournals,
  useUnassignedChildJournals,
  useGiftCertificates,
  useAllocation,
  revalidateDashboard,
  serverErrorMessage,
  useMyAllChildJournals,
} from './hooks';
import { patchChildJournal } from 'child-journals/api';
import { useGetCurrentMember } from 'auth/hooks';
import { togglefirstTimeUserFlowVisible } from 'stories/actions';
import { useDispatch } from 'react-redux';

const STORE_URL = 'https://babyyearbooks.com';

const AccountDashboard = () => {
  const { push } = useHistory();
  // Each tab is its own route (/account/unassigned | /account/my-books |
  // /account/gifts), so a refresh — or a link shared / returned to after the
  // set-up flow — lands on the same screen.
  const { tab } = useParams();
  const activeTab = TAB_ORDER.includes(tab) ? tab : TAB_UNASSIGNED;
  const { data } = useGetCurrentMember()

  const { entitlements: unassignedBooks, isLoading: booksLoading } = useUnassignedBookEntitlements();
  const { entitlements: unassignedAddOns, isLoading: addOnsLoading } = useUnassignedAddOnEntitlements();
  const { journals, isLoading: journalsLoading } = useMyChildJournals();
  const { journals: allJournals } = useMyAllChildJournals();

  const {
    journals: unassignedJournalsTemp,
    isLoading: unassignedJournalsLoading,
  } = useUnassignedChildJournals();
  const { giftCertificates, isLoading: giftsLoading } = useGiftCertificates();
  const unassignedJournals = unassignedJournalsTemp.filter(journal => !!journal.title)

  const hasAssignedJournal = allJournals.some((journal) => journal?.state === "initialized");

  const PURCHASE_URL = data?.data?.data?.attributes?.['baby-yearbook-purchase-url'];

  // Nothing is created on load. "Confirm" assigns add-ons (creating the book's
  // journal if needed); "Set up" assigns the book entitlement to that same journal.
  const {
    confirmAddOns,
    assignBookToJournal,
    busyBookId,
    isConfirming,
    isBusy,
    error: allocationError,
  } = useAllocation();

  const setActiveTab = nextTab => push(`/account/${nextTab}`);

  // Checkbox selection in "Your purchased add-on(s)" — local only.
  const [selectedAddOnIds, setSelectedAddOnIds] = useState([]);
  // bookEntitlementId -> [addOnEntitlementId], staged but not yet sent to the API.
  const [pendingByBook, setPendingByBook] = useState({});
  // bookEntitlementId -> [add-on record], assigned to the journal by Confirm.
  // The records are kept rather than the ids because a confirmed add-on drops out
  // of the unassigned list, so there'd be nothing left to read a label from.
  const [confirmedByBook, setConfirmedByBook] = useState({});
  // bookEntitlementId -> journalId. Created on demand by Confirm and reused by
  // Set up, so a book never gets a second journal.
  const [journalIdByBook, setJournalIdByBook] = useState({});
  const [details, setDetails] = useState(null);

  const addOnById = useMemo(
    () => Object.fromEntries(unassignedAddOns.map(addOn => [addOn.id, addOn])),
    [unassignedAddOns]
  );

  const pendingAddOnIds = useMemo(
    () => Object.values(pendingByBook).flat(),
    [pendingByBook]
  );

  const confirmedAddOnIds = useMemo(
    () => Object.values(confirmedByBook).flat().map(addOn => addOn.id),
    [confirmedByBook]
  );

  // Add-ons staged onto a book drop out of the picker (they're shown on the tile).
  // Confirmed ones are filtered too: the server stops returning them as unassigned,
  // but this avoids them flickering back into the list until that refetch lands.
  const selectableAddOns = useMemo(
    () =>
      unassignedAddOns.filter(
        addOn => !pendingAddOnIds.includes(addOn.id) && !confirmedAddOnIds.includes(addOn.id)
      ),
    [unassignedAddOns, pendingAddOnIds, confirmedAddOnIds]
  );

  // While add-ons are ticked, each book's CTA becomes "Add to this book"; with
  // nothing ticked it reverts to "Set up", which is the button that commits.
  const hasSelection = selectedAddOnIds.length > 0;
  const isAddOnsMode = hasSelection || pendingAddOnIds.length > 0;

  // A gift certificate the giver also claimed themselves (claimerId === memberId)
  // is effectively their own book, not a gift to someone else — it belongs in My
  // Books, not the Gifts tab.
  const isSelfClaimedGift = gift => !!gift.claimerId && !!gift.memberId && gift.claimerId === gift.memberId;

  // Excludes self-claimed gifts' journals, so they aren't hidden from My Books.
  const giftedJournalIds = useMemo(
    () =>
      new Set(
        giftCertificates
          .filter(gift => !isSelfClaimedGift(gift))
          .map(gift => gift.childJournalId)
          .filter(Boolean)
      ),
    [giftCertificates]
  );

  // Hides self-claimed gifts from the Gifts tab itself.
  const visibleGiftCertificates = useMemo(
    () => giftCertificates.filter(gift => !isSelfClaimedGift(gift)),
    [giftCertificates]
  );

  // A journal created by Confirm exists before its book is assigned. The API now
  // reports those under section=unassigned, so that list — rather than session
  // state — is what keeps them out of My Books (and it survives a refresh). The
  // in-session ids are still unioned in to cover the gap before the refetch lands.
  const awaitingBookAssignment = useMemo(
    () =>
      new Set([
        ...unassignedJournals.map(journal => journal.id),
        ...Object.values(journalIdByBook),
      ]),
    [unassignedJournals, journalIdByBook]
  );
  const myBooks = useMemo(
    () =>
      journals.filter(
        journal =>
          !giftedJournalIds.has(journal.id) && !awaitingBookAssignment.has(journal.id)
      ),
    [journals, giftedJournalIds, awaitingBookAssignment]
  );

  // Repeat gifts to the same recipient get "- 2", "- 3" appended.
  const giftSuffixes = useMemo(() => {
    const seen = {};
    return visibleGiftCertificates.map(gift => {
      const key = `${gift.recipientName || ''}|${gift.babyName || ''}`;
      seen[key] = (seen[key] || 0) + 1;
      return seen[key];
    });
  }, [visibleGiftCertificates]);

  // Same convention for My Books: the API has no child/parent relationship, so
  // two journals for the same child (or just a name collision) are only
  // distinguishable here by "- 2", "- 3", … appended to the repeat. Numbered
  // oldest-first regardless of the list's own display order, so the earliest
  // journal keeps the plain name and later ones pick up the suffix.
  const bookSuffixByJournalId = useMemo(() => {
    const seen = {};
    const suffixes = {};
    [...myBooks]
      .sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0))
      .forEach(journal => {
        const key = (journal.firstName || '').trim().toLowerCase();
        seen[key] = (seen[key] || 0) + 1;
        suffixes[journal.id] = seen[key];
      });
    return suffixes;
  }, [myBooks]);

  const counts = {
    [TAB_UNASSIGNED]: unassignedJournals.length,
    [TAB_MY_BOOKS]: myBooks.length,
    [TAB_GIFTS]: visibleGiftCertificates.length,
  };

  const toggleAddOn = id =>
    setSelectedAddOnIds(current =>
      current.includes(id) ? current.filter(existing => existing !== id) : [...current, id]
    );

  // Stages the ticked add-ons onto this book. Frontend state only — nothing is
  // assigned until "Confirm".
  const addSelectionToBook = bookEntitlementId => {
    if (!selectedAddOnIds.length) return;
    setPendingByBook(current => ({
      ...current,
      [bookEntitlementId]: [...(current[bookEntitlementId] || []), ...selectedAddOnIds],
    }));
    setSelectedAddOnIds([]);
  };

  const removePending = addOnId =>
    setPendingByBook(current => {
      const next = {};
      Object.entries(current).forEach(([bookId, ids]) => {
        const remaining = ids.filter(id => id !== addOnId);
        if (remaining.length) next[bookId] = remaining;
      });
      return next;
    });

  const exitAddOnsMode = () => {
    setSelectedAddOnIds([]);
    setPendingByBook({});
  };
  const isFromMobile = useMobileCheckHook()

  // "Confirm >" — assigns the staged add-ons AND the book entitlement to the
  // journal, in one atomic bulk call.
  //
  // Once the book entitlement is assigned it drops out of the unassigned-books
  // list, but its journal is still `state: unassigned` — so the tile reappears in
  // this same tab as an unassigned-journal tile, still offering "Set up". Set up
  // then takes it through the flow that flips the journal to `assigned`.
  const onConfirm = async () => {
    if (!pendingAddOnIds.length) return;

    const staged = pendingByBook;
    // Any key that is already a journal id — an unassigned journal, a My Books
    // journal, or a gift's journal — is its own target. Seeding these means
    // confirmAddOns reuses the existing journal instead of creating one.
    const journalTargets = {
      ...Object.fromEntries(unassignedJournals.map(journal => [journal.id, journal.id])),
      ...Object.fromEntries(journals.map(journal => [journal.id, journal.id])),
      ...Object.fromEntries(
        giftCertificates
          .filter(gift => gift.childJournalId)
          .map(gift => [gift.childJournalId, gift.childJournalId])
      ),
      ...journalIdByBook,
    };
    // Only book-entitlement keys get their entitlement assigned; journal-tile keys
    // have no book entitlement behind them.
    const bookEntitlementIds = unassignedBooks
      .map(entitlement => entitlement.id)
      .filter(id => staged[id]?.length);

    const result = await confirmAddOns(staged, journalTargets, bookEntitlementIds);
    // Only remember journals created for a book entitlement. The self-mapped
    // journal targets above must not land here: this map also drives the
    // "awaiting book assignment" filter, so an existing My Books journal would
    // otherwise be hidden from its own tab.
    const bookKeys = new Set(unassignedBooks.map(entitlement => entitlement.id));
    setJournalIdByBook(
      Object.fromEntries(
        Object.entries(result.journalIds).filter(([key]) => bookKeys.has(key))
      )
    );
    if (!result.ok) return;

    // Promote the staged (yellow) chips to assigned (outlined) pills.
    setConfirmedByBook(current => {
      const next = { ...current };
      Object.entries(staged).forEach(([bookId, addOnIds]) => {
        const records = addOnIds.map(id => addOnById[id]).filter(Boolean);
        next[bookId] = [...(next[bookId] || []), ...records];
      });
      return next;
    });
    setPendingByBook({});
    setSelectedAddOnIds([]);
  };

  // "Set up" — assigns the book entitlement to its journal, reusing the one
  // Confirm created, then opens the existing set-up flow. This is what moves the
  // book out of Unassigned and into My Books.
  const onSetUp = async (bookEntitlementId, { navigate = true, force = false } = {}) => {
    if (!force && isBusy) return;

    const result = await assignBookToJournal(
      bookEntitlementId,
      journalIdByBook[bookEntitlementId]
    );
    if (!result.ok) return;

    // The book is assigned now, so its local staging state is no longer needed.
    setConfirmedByBook(current => {
      const next = { ...current };
      delete next[bookEntitlementId];
      return next;
    });
    setJournalIdByBook(current => {
      const next = { ...current };
      delete next[bookEntitlementId];
      return next;
    });

    if (navigate) {
      push(`/account/setup/${result.journalId}?entitlementId=${bookEntitlementId}`);
    }

    return result;
  };

  const [isSettingUp, setIsSettingUp] = useState(false);

  // Creates a journal for each unassigned book on entry: every book exactly once,
  // and every book eventually.
  //
  // The hard part is that doing the work changes the input. `onSetUp` assigns the
  // book entitlement and calls revalidateDashboard(), so `unassignedBooks` shrinks
  // mid-flight and the effect re-runs. An earlier version cancelled the in-flight
  // loop from the effect's cleanup on each of those re-runs, which meant only
  // whichever book happened to be in flight got processed per generation, and the
  // cancelled run skipped `setIsSettingUp(false)` — so the last generation to be
  // cancelled left the dashboard spinning forever.
  //
  // So: one drain loop, never cancelled, that re-reads the latest list each turn.
  //
  //  * `isDrainingRef` is a ref, not state, so it gates re-entry synchronously
  //    within the same tick. A concurrent effect run sees the drain already going
  //    and returns; the running loop picks up its books via the ref below.
  //  * `unassignedBooksRef` always holds the newest list, so the loop sees books
  //    that arrive while it runs and terminates only when none are left.
  //  * each id is marked attempted immediately BEFORE its await, so an id can
  //    never be marked and then skipped (the previous version marked the whole
  //    batch up front, so a cancelled run lost every id it hadn't reached).
  //  * `finally` always clears the flag, so no path can strand the spinner.
  const attemptedBookIds = useRef(new Set());
  const isDrainingRef = useRef(false);
  const isMountedRef = useRef(true);
  const dispatch = useDispatch();

  const onSetUpRef = useRef(onSetUp);
  onSetUpRef.current = onSetUp;
  const unassignedBooksRef = useRef(unassignedBooks);
  unassignedBooksRef.current = unassignedBooks;

  const unassignedBookIdsKey = useMemo(
    () => unassignedBooks.map(entitlement => entitlement.id).join(','),
    [unassignedBooks]
  );

  useEffect(() => () => {
    isMountedRef.current = false;
  }, []);

  useEffect(() => {
    // Already draining — that loop will see these books through the ref.
    if (isDrainingRef.current) return;

    const nextBookId = () => {
      const next = unassignedBooksRef.current.find(
        entitlement => !attemptedBookIds.current.has(entitlement.id)
      );
      return next && next.id;
    };

    if (!nextBookId()) return;

    isDrainingRef.current = true;
    setIsSettingUp(true);

    (async () => {
      try {
        for (let id = nextBookId(); id; id = nextBookId()) {
          attemptedBookIds.current.add(id);
          // eslint-disable-next-line no-await-in-loop
          await onSetUpRef.current(id, { navigate: false, force: true });
        }
      } finally {
        isDrainingRef.current = false;
        if (isMountedRef.current) setIsSettingUp(false);
      }
    })();
  }, [unassignedBookIdsKey]);

  // "Set up" on an unassigned JOURNAL. The journal already exists, so there's no
  // entitlement to assign here — it goes straight into the set-up flow, which
  // flips the journal to `assigned` once a relationship is chosen.
  const onSetUpJournal = async journalId => {
    if (isBusy) return;

    const stagedAddOnIds = pendingByBook[journalId] || [];
    if (stagedAddOnIds.length) {
      // Staged add-ons attach to this journal directly — it needs no creating.
      const result = await confirmAddOns({ [journalId]: stagedAddOnIds }, { [journalId]: journalId });
      if (!result.ok) return;
      setPendingByBook(current => {
        const next = { ...current };
        delete next[journalId];
        return next;
      });
    }

    push(`/account/setup/${journalId}`);
  };

  // "Start my book" — a journal with no stories yet moves assigned → initialized.
  const onStartBook = async journal => {
    if (journal.storyCount === 0) {
      try {
        await patchChildJournal({ id: journal.id, state: 'initialized' });
        dispatch(togglefirstTimeUserFlowVisible(true))

        revalidateDashboard();
      } catch (err) {
        // Don't block the customer from opening their book over this.
      }
    }
    push(`/child-journals/${journal.id}`);
  };

  const goToStore = () => window.open(STORE_URL, '_blank', 'noopener');
  const goToRedeem = () => push('/redeem-gift-certificate');

  // In-app purchasing (upgrades, add-ons, extra pages/copies, shipping) for one
  // specific journal — see src/account-dashboard/purchases. `title` carries the
  // display name the card already had on hand; `from` is the tab Back/Done
  // returns to. `mode=upgrade` scopes the shelf to just the upgrade group —
  // "Upgrade Bundle" is a separate, deliberate entry point from "Add-ons".
  const goToPurchase = (journalId, title, from, mode) =>
    push(
      `/account/purchase/${journalId}?title=${encodeURIComponent(title || '')}&from=${from}${mode ? `&mode=${mode}` : ''
      }`
    );
  const onPurchaseBook = (journal, isFromMyBooks = true) => goToPurchase(journal.id, isFromMyBooks ? journal.firstName : journal.title, isFromMyBooks ? TAB_MY_BOOKS : TAB_UNASSIGNED);
  const onPurchaseGift = (gift, suffix) => goToPurchase(gift.childJournalId, giftTitle(gift, suffix), TAB_GIFTS);
  const onUpgradeBundle = (journal, isFromMyBooks = true) => goToPurchase(journal.id, isFromMyBooks ? journal.firstName : journal.title, isFromMyBooks ? TAB_MY_BOOKS : TAB_UNASSIGNED, 'upgrade');
  const onUpgradeGiftBundle = (gift, suffix) =>
    goToPurchase(gift.childJournalId, giftTitle(gift, suffix), TAB_GIFTS, 'upgrade');

  const isLoading = isSettingUp || booksLoading || addOnsLoading || journalsLoading || unassignedJournalsLoading || giftsLoading;

  const isSideBarClosed = useIsSideBarClosed();

  const renderTab = () => {
    if (activeTab === TAB_UNASSIGNED) {
      // The tab holds two datasets: the unassigned book entitlements and the
      // journals the API reports as unassigned.
      if (!unassignedBooks.length && !unassignedJournals.length) {
        return <EmptyState variant="unassigned" onExplore={goToStore} />;
      }
      const chipsFor = key =>
        (pendingByBook[key] || []).map(id => addOnById[id]).filter(Boolean);

      return (
        <CardGrid>
          {/* {unassignedBooks.map((entitlement, index) => (
            <UnassignedBookTile
              key={entitlement.id}
              entitlement={entitlement}
              index={index}
              assignedChips={confirmedByBook[entitlement.id] || []}
              pendingChips={chipsFor(entitlement.id)}
              isAddOnsMode={isAddOnsMode}
              hasSelection={hasSelection}
              isSettingUp={busyBookId === entitlement.id}
              disabled={isBusy}
              onSetUp={onSetUp}
              onAddToBook={addSelectionToBook}
              onRemovePending={removePending}
              onDetails={() =>
                setDetails({ title: entitlement.title, entitlements: entitlement.entitlements })
              }
            />
          ))} */}
          {unassignedJournals.sort((a, b) => {
            const dateA = new Date(a['createdAt']);
            const dateB = new Date(b['createdAt']);
            return dateA - dateB;
          }).filter(journal => !!journal.title).map((journal, index) => (
            <UnassignedJournalTile
              key={journal.id}
              journal={journal}
              bookNumber={unassignedBooks.length + index + 1}
              assignedChips={confirmedByBook[journal.id] || []}
              pendingChips={chipsFor(journal.id)}
              isAddOnsMode={isAddOnsMode}
              hasSelection={hasSelection}
              disabled={isBusy}
              onSetUp={onSetUpJournal}
              onAddToBook={addSelectionToBook}
              onRemovePending={removePending}
              onPurchase={onPurchaseBook}
              onUpgradeBundle={onUpgradeBundle}
            />
          ))}
        </CardGrid>
      );
    }

    if (activeTab === TAB_MY_BOOKS) {
      if (!myBooks.length) return <EmptyState variant="my-books" onExplore={goToStore} />;
      return (
        <CardGrid>
          {myBooks.map(journal => (
            <BookCard
              key={journal.id}
              journal={journal}
              titleSuffix={bookSuffixByJournalId[journal.id]}
              isAddOnsMode={isAddOnsMode}
              hasSelection={hasSelection}
              pendingChips={(pendingByBook[journal.id] || [])
                .map(id => addOnById[id])
                .filter(Boolean)}
              disabled={isBusy}
              onDetails={() =>
                setDetails({ title: journal.firstName, entitlements: journal.entitlements })
              }
              onStart={onStartBook}
              onAddToBook={addSelectionToBook}
              onRemovePending={removePending}
              onPurchase={onPurchaseBook}
              onUpgradeBundle={onUpgradeBundle}
            />
          ))}
        </CardGrid>
      );
    }

    if (!visibleGiftCertificates.length) return <EmptyState variant="gifts" onExplore={goToStore} />;
    return (
      <CardGrid>
        {visibleGiftCertificates.map((gift, index) => (
          <GiftCard
            key={gift.id}
            gift={gift}
            isAddOnsMode={isAddOnsMode}
            titleSuffix={giftSuffixes[index]}
            hasSelection={hasSelection}
            pendingChips={(pendingByBook[gift.childJournalId] || [])
              .map(id => addOnById[id])
              .filter(Boolean)}
            disabled={isBusy}
            onAddToBook={addSelectionToBook}
            onRemovePending={removePending}
            onDetails={() =>
              setDetails({ title: gift.recipientName || 'Gift', entitlements: gift.entitlements })
            }
            // "Edit certificate" returns the giver to "Who are you gifting this to?"
            // The certificate id alone identifies the record; the flow reads the
            // rest (recipient, journal) back off GET /v3/commerce/gift_certificates.
            onEditCertificate={() => push(`/gift?certificateId=${gift.id}`)}
            onPurchase={() => onPurchaseGift(gift, giftSuffixes[index])}
            onUpgradeBundle={() => onUpgradeGiftBundle(gift, giftSuffixes[index])}
          />
        ))}
      </CardGrid>
    );
  };

  return (
    <Page>
      <DashboardTopNav PURCHASE_URL={PURCHASE_URL} onRedeemGift={goToRedeem} />

      <Content style={(!hasAssignedJournal || isFromMobile) ? {} : { margin: isSideBarClosed ? "0 auto" : "0 0 0 285px" }}>
        <Heading>My Account</Heading>

        <AddOnSelector
          addOns={selectableAddOns}
          selectedIds={selectedAddOnIds}
          onToggle={toggleAddOn}
          locked={isBusy}
        />

        <DashboardTabs activeTab={activeTab} isLoading={isLoading} counts={counts} onChange={setActiveTab} />

        {isLoading ? <Loading local /> : renderTab()}

        {!!allocationError && (
          <ErrorText>
            {serverErrorMessage(allocationError, "We couldn't save that. Please try again.")}
          </ErrorText>
        )}
      </Content>

      {isAddOnsMode && (
        <ConfirmBar>
          <BackButton type="button" onClick={exitAddOnsMode}>
            <Chevron direction="left" />
            Back
          </BackButton>
          {/* Assigns the staged add-ons only — the book stays unassigned. */}
          <PrimaryButton
            inline
            type="button"
            disabled={!pendingAddOnIds.length || isBusy}
            onClick={onConfirm}
          >
            {isConfirming ? 'Saving' : 'Confirm'}
            <Chevron />
          </PrimaryButton>
        </ConfirmBar>
      )}
      {/* : (
        <DashboardBottomNav PURCHASE_URL={PURCHASE_URL} onRedeemGift={goToRedeem} />
      ) */}

      {!!details && (
        <DetailsModal
          title={details?.entitlements?.['plan-name']}
          bundleName={details.bundleName}
          entitlements={details.entitlements}
          onClose={() => setDetails(null)}
        />
      )}
    </Page>
  );
};

export default AccountDashboard;
